import React from "react";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";
import { Tabs } from 'antd';
import type { TabsProps } from 'antd';
import { UiContext } from "../contexts/ui";
import { exportStore, importStore, resetStore } from "../db/action";
import { useKeyPress } from "../hooks";
import SettingBackground from "./SettingBackground";
import Persist from "./Persist";
import "./Settings.sass";
import System from "./System";
import SettingWidgets from "./SettingWidgets";
import DownloadTab from "./DownloadTab";

const messages = defineMessages({
  background: {
    id: "background",
    description: "set background",
    defaultMessage: "Background",
  },
  widgets: {
    id: 'widgets',
    description: 'set widgets',
    defaultMessage: 'Widgets',
  },
  settings: {
    id: 'settings',
    description: 'set settings',
    defaultMessage: 'Settings',
  },
  download: {
    id: 'download',
    description: 'share or download',
    defaultMessage: 'Download',
  }
});

const Settings: React.FC = () => {
  const intl = useIntl();
  const { toggleSettings } = React.useContext(UiContext);
  const protocol = window.location.protocol;

  const handleReset = () => {
    if (
      confirm(
        "Are you sure you want to delete all of your Tabliss settings? This cannot be undone.",
      )
    )
      resetStore();
  };

  const handleExport = () => {
    const json = exportStore();
    const url = URL.createObjectURL(
      new Blob([json], { type: "application/json" }),
    );

    const a = document.createElement("a");
    document.body.appendChild(a);
    a.style.display = "none";
    a.href = url;
    a.download = "tabliss.json";
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handleImport = () => {
    const input = document.createElement("input");
    document.body.appendChild(input);
    input.style.display = "none";
    input.type = "file";
    input.addEventListener("change", function () {
      if (this.files) {
        const file = this.files[0];
        const reader = new FileReader();
        reader.addEventListener("load", (event) => {
          if (event.target && event.target.result) {
            try {
              const state = JSON.parse(event.target.result as string);
              importStore(state);
            } catch (error) {
              alert(
                `Invalid import file: ${
                  error instanceof Error ? error.message : "Uknown error"
                }`,
              );
            }
          }
        });
        reader.readAsText(file);
      }
      document.body.removeChild(input);
    });
    input.click();
  };

  console.log('settings=', intl.formatMessage(messages.settings));
  console.log('download=', intl.formatMessage(messages.download));

  const items: TabsProps['items'] = [
    {
      key: '1',
      label: intl.formatMessage(messages.background),
      children: <SettingBackground />,
    },
    {
      key: '2',
      label: intl.formatMessage(messages.widgets),
      children: <SettingWidgets />,
    },
    {
      key: '3',
      label: intl.formatMessage(messages.settings),
      children: <>
        <System />
        <p style={{ marginBottom: "2rem" }}>
          <a onClick={handleImport}>Import</a>,{" "}
          <a onClick={handleExport}>export</a> or{" "}
          <a onClick={handleReset}>reset</a> your settings
        </p>

        { protocol.startsWith('http') ? null : <Persist /> }
      </>,
    },
    {
      key: '4',
      label: intl.formatMessage(messages.download),
      children: <DownloadTab />,
    }
  ];

  useKeyPress(toggleSettings, ["Escape"]);

  return (
    <div className="Settings" style={{ margin: '0 5px' }}>
      <div>
        <Tabs defaultActiveKey="1" items={items} />
        <FormattedMessage
          id="settings.translationCredits"
          description="Give yourself some credit :)"
          defaultMessage=" "
          tagName="p"
        />
      </div>
    </div>
  );
};

export default React.memo(Settings);
